import { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api, { setOnUnauthorized } from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import moment from "moment";
import {
  getDaysUntilDueDate,
  isCompanyFinanceOnly,
  isServerAccessPolicyEnforced,
} from "../../utils/companyAccess";
import { mergeAuthenticatedUserUpdate } from "../../utils/rolloutFeatures";

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [socket, setSocket] = useState(null);
  
  // Ref para manter referência dos listeners ativos
  const listenersRef = useRef(new Set());

  // Registrar callback de 401 no api.js (interceptors vivem lá, no nível do módulo)
  useEffect(() => {
    setOnUnauthorized(() => {
      setIsAuth(false);
      if (history.location.pathname !== "/login") {
        history.push("/login");
      }
    });
    return () => setOnUnauthorized(null);
  }, [history]);

  // Effect para inicialização do token
  useEffect(() => {
    // Versoes antigas usavam localStorage compartilhado para entregar o token
    // de impersonacao a uma nova aba. Isso permitia que a aba do ADM Master
    // capturasse a sessao por corrida. O fluxo atual escreve diretamente na
    // sessionStorage da nova aba; remova somente qualquer marcador legado.
    localStorage.removeItem("_estratizaImpersonate");
    const token = sessionStorage.getItem("token") || localStorage.getItem("token");
    const storedRefresh = sessionStorage.getItem("refreshToken") || localStorage.getItem("refreshToken");
    (async () => {
      if (token || storedRefresh) {
        try {
          const { data } = await api.post("/auth/refresh_token",
            storedRefresh ? { refreshToken: storedRefresh } : {}
          );
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
          if (data.token && typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("estratiza-auth-token-refreshed", {
              detail: { token: `Bearer ${data.token}` }
            }));
          }
          // Salva novo access token no storage correto
          if (data.token) {
            if (sessionStorage.getItem("token")) {
              sessionStorage.setItem("token", JSON.stringify(data.token));
            } else {
              localStorage.setItem("token", JSON.stringify(data.token));
            }
          }
          if (data.refreshToken) {
            if (sessionStorage.getItem("token")) {
              sessionStorage.setItem("refreshToken", data.refreshToken);
            } else {
              localStorage.setItem("refreshToken", data.refreshToken);
            }
          }
          setUser(data.user || data);
          setIsAuth(true);
        } catch (err) {
          // Erro de autenticacao (401/403): tokens invalidos → limpar e ir para login
          // Erro de rede (sem err.response): servidor indisponivel → NAO expulsar
          if (err?.response?.status === 401 || err?.response?.status === 403) {
            localStorage.removeItem("token");
            localStorage.removeItem("refreshToken");
            sessionStorage.removeItem("token");
            sessionStorage.removeItem("refreshToken");
          } else {
            // Rede indisponivel — tenta usar token existente mesmo sem refresh
            const rawToken = (() => {
              try { const t = sessionStorage.getItem("token") || localStorage.getItem("token"); return t ? JSON.parse(t) : null; }
              catch { return sessionStorage.getItem("token") || localStorage.getItem("token"); }
            })();
            if (rawToken) {
              api.defaults.headers.Authorization = `Bearer ${rawToken}`;
              setIsAuth(true);
              // Tenta obter dados do usuario via endpoint autenticado
              try {
                const { data: ud } = await api.get("/users/me").catch(() => api.get("/auth/me")).catch(() => ({ data: null }));
                if (ud) setUser(ud);
              } catch (_) {}
            }
          }
          if (!err?.response || (err?.response?.status !== 401 && err?.response?.status !== 403)) {
            // so mostra erro se nao for de auth (ja limpou o estado acima)
          } else {
            toastError(err);
          }
        }
      }
      setLoading(false);
    })();
  }, []);

  // Effect para configuração do socket
  useEffect(() => {
    if (
      Object.keys(user).length &&
      user.id > 0 &&
      !isCompanyFinanceOnly(user)
    ) {
      console.log("Configurando socket para user", user.id, "company", user.companyId);
      
      // Limpar listeners anteriores
      if (socket) {
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
      }

      // Criar nova conexão socket
      const socketInstance = socketConnection({ user: {
        companyId: user.companyId,
        id: user.id }
      });
      
      if (socketInstance) {
        setSocket(socketInstance);

        // Aguardar um pouco para garantir que o socket está configurado
        setTimeout(() => {
          const eventName = `company-${user.companyId}-user`;
          
          const handleUserUpdate = (data) => {
            if (data.action === "update" && data.user.id === user.id) {
              setUser(currentUser =>
                mergeAuthenticatedUserUpdate(currentUser, data.user)
              );
            }
          };

          // Verificar se o socket tem o método 'on'
          if (socketInstance && typeof socketInstance.on === 'function') {
            socketInstance.on(eventName, handleUserUpdate);
            listenersRef.current.add(eventName);
            console.log(`Listener adicionado para: ${eventName}`);
          } else {
            console.error("Socket instance não tem método 'on'", socketInstance);
          }
        }, 100);
      }
    }

    // Cleanup function
    return () => {
      if (socket && listenersRef.current.size > 0) {
        console.log("Limpando listeners do socket para user", user.id);
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
      }
    };
  }, [user.id, user.companyId, user?.companyAccess?.state]);

  // Effect para buscar dados do usuário atual
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data.user || data);
      } catch (err) {
        console.log("Erro ao buscar usuário atual:", err);
      }
    };
    
    if (isAuth) {
      fetchCurrentUser();
    }
  }, [isAuth]);

  // Verificacao de vencimento -- checa dueDate a cada 5 min e bloqueia automaticamente
  useEffect(() => {
    if (!isAuth) return;

    const checkExpiration = async () => {
      try {
        const { data } = await api.get("/auth/me");
        const currentUser = data.user || data;
        setUser(currentUser);
        if (isServerAccessPolicyEnforced(currentUser)) {
          if (isCompanyFinanceOnly(currentUser)) {
            const vencimento = currentUser?.companyAccess?.dueDate
              ? moment(currentUser.companyAccess.dueDate).format("DD/MM/YYYY")
              : "data nao informada";
            toastError(
              `Sua assinatura esta restrita desde ${vencimento}. Acesse o Financeiro.`
            );
            history.push("/financeiro-aberto");
          }
          return;
        }
        // Empresa 1 (master) nunca vence
        if (!currentUser?.company?.dueDate || currentUser?.company?.id === 1) return;
        const hojeInicio = moment().startOf("day");
        const vencimentoInicio = moment(currentUser.company.dueDate).startOf("day");
        const isExpired = hojeInicio.isAfter(vencimentoInicio, "day");
        if (isExpired) {
          const vencimento = moment(currentUser.company.dueDate).format("DD/MM/YYYY");
          toastError(`Sua assinatura venceu em ${vencimento}. Entre em contato com o suporte!`);
          history.push("/financeiro-aberto");
        }
      } catch (_err) {
        // silencioso — nao expulsar por erro de rede
      }
    };

    // Checar imediatamente ao logar e depois a cada 5 minutos
    checkExpiration();
    const expirationInterval = setInterval(checkExpiration, 5 * 60 * 1000);
    return () => clearInterval(expirationInterval);
  }, [isAuth]);

  const handleLogin = async (userData) => {
    setLoading(true);

    try {
      const { data } = await api.post("/auth/login", userData);
      const {
        user: { company },
      } = data;

      // Lógica de configurações da empresa (mantém como estava)
      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null);
        }
      }

      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "sendSignMessage"
        );

        const signEnable = setting.value === "enable";

        if (setting && setting.value === "enabled") {
          localStorage.setItem("sendSignMessage", signEnable);
        }
      }
      
      localStorage.setItem("profileImage", data.user.profileImage);

      if (isServerAccessPolicyEnforced(data.user)) {
        localStorage.setItem("token", JSON.stringify(data.token));
        if (data.refreshToken) {
          localStorage.setItem("refreshToken", data.refreshToken);
        }
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setUser(data.user);
        setIsAuth(true);

        if (isCompanyFinanceOnly(data.user)) {
          const dueDate = data.user?.companyAccess?.dueDate;
          const vencimento = dueDate
            ? moment(dueDate).format("DD/MM/YYYY")
            : "data nao informada";
          toastError(
            `Sua assinatura esta restrita desde ${vencimento}. Acesse o Financeiro.`
          );
          history.push("/financeiro-aberto");
        } else {
          toast.success(i18n.t("auth.toasts.success"));
          const daysUntilDueDate = getDaysUntilDueDate(data.user);
          if (
            typeof daysUntilDueDate === "number" &&
            daysUntilDueDate >= 0 &&
            daysUntilDueDate < 5
          ) {
            toast.warn(
              `Sua assinatura vence em ${daysUntilDueDate} ${
                daysUntilDueDate === 1 ? "dia" : "dias"
              }`
            );
          }
          history.push("/tickets");
        }
        setLoading(false);
        return;
      }

      moment.locale("pt-br");
      let dueDate;
      if (data.user.company.id === 1) {
        dueDate = "2999-12-31T00:00:00.000Z";
      } else {
        dueDate = data.user.company.dueDate;
      }
      
      const hoje = moment(moment()).format("DD/MM/yyyy");
      const vencimento = moment(dueDate).format("DD/MM/yyyy");

      // Comparar apenas as datas (sem horas) para permitir acesso até 23h59 do dia do vencimento
      const hojeInicio = moment().startOf('day');
      const vencimentoInicio = moment(dueDate).startOf('day');
      
      var diff = vencimentoInicio.diff(hojeInicio, 'days');
      var before = hojeInicio.isSameOrBefore(vencimentoInicio, 'day');
      var dias = diff;

      if (before === true) {
        localStorage.setItem("token", JSON.stringify(data.token));
        localStorage.setItem("companyDueDate", vencimento);
        if (data.refreshToken) {
          localStorage.setItem("refreshToken", data.refreshToken);
        }
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setUser(data.user || data);
        setIsAuth(true);
        toast.success(i18n.t("auth.toasts.success"));
        
        if (Math.round(dias) < 5) {
          toast.warn(
            `Sua assinatura vence em ${Math.round(dias)} ${
              Math.round(dias) === 1 ? "dia" : "dias"
            } `
          );
        }

        history.push("/tickets");
        setLoading(false);
      } else {
        // Salva tokens mesmo com assinatura vencida (sem isso a sessao some ao atualizar)
        localStorage.setItem("token", JSON.stringify(data.token));
        if (data.refreshToken) {
          localStorage.setItem("refreshToken", data.refreshToken);
        }
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setUser(data.user || data);
        setIsAuth(true);
        toastError(`Opss! Sua assinatura venceu ${vencimento}.
Entre em contato com o Suporte para mais informações! `);
        history.push("/financeiro-aberto");
        setLoading(false);
      }
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      // Limpar socket antes do logout
      if (socket) {
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
        
        if (typeof socket.disconnect === 'function') {
          socket.disconnect();
        }
      }

      await api.delete("/auth/logout");
      setIsAuth(false);
      setUser({});
      setSocket(null);
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("cshow");
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("refreshToken");
      api.defaults.headers.Authorization = undefined;
      setLoading(false);
      history.push("/login");
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      console.log(data);
      return data;
    } catch (_) {
      return null;
    }
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
    socket,
  };
};

export default useAuth;
